/** EGB-00002 — Enterprise Engineering Standards Framework (mirrors pkg/engineeringgovernance) */

export const EGB_ENG_ID = 'EGB-00002';

export interface CouncilMemberRow {
  role: string;
  responsibility: string;
  approvalScope: string;
}

export interface CodingStandardRow {
  standard: string;
  rule: string;
  status: string;
}

export interface StackStandardRow {
  stack: string;
  standard: string;
  status: string;
}

export interface DomainStandardRow {
  domain: string;
  standard: string;
  status: string;
}

export interface PolicyRow {
  area: string;
  policy: string;
  status: string;
}

export interface ReviewCheckRow {
  area: string;
  check: string;
  status: string;
  score: number;
}

export interface SecureTestRow {
  domain: string;
  requirement: string;
  status: string;
  coverage: string;
}

export interface CIMetricRow {
  category: string;
  item: string;
  value: string;
  status: string;
}

export const COUNCIL_ROLES = [
  'CTO', 'Chief Enterprise Architect', 'Engineering Manager', 'Technical Leads',
  'AI Engineering Lead', 'Security Engineering Lead', 'DevOps Lead', 'QA Lead',
  'Database Architect', 'Frontend Architect', 'Backend Architect', 'Accessibility Lead',
];

export const COUNCIL_FUNCTIONS = ['Engineering Council', 'Roles', 'Responsibilities', 'Approval Matrix', 'Review Process'];

export const CODING_STANDARDS = [
  'Naming Conventions', 'Folder Structures', 'Package Structures', 'Class Design', 'Configuration Standards', 'Documentation Standards',
];

export const BACKEND_AREAS = [
  'Language Standards', 'Dependency Injection', 'Error Handling', 'Transactions', 'Concurrency', 'Logging', 'Observability',
];

export const FRONTEND_AREAS = [
  'React Standards', 'TypeScript Standards', 'Component Architecture', 'State Management', 'Performance', 'Accessibility',
];

export const DATABASE_AREAS = [
  'Schema Standards', 'Migration Standards', 'Index Policies', 'Transaction Policies', 'Retention Policies', 'Audit Standards',
];

export const API_AREAS = [
  'REST APIs', 'Versioning', 'Error Handling', 'Pagination', 'Filtering', 'Sorting', 'Rate Limiting', 'Documentation',
];

export const AI_ENGINEERING = [
  'AI Coding Assistants', 'Prompt Standards', 'Generated Code Validation', 'Human Review', 'Architecture Compliance', 'Security Validation',
];

export const GIT_GOVERNANCE = [
  'Branch Strategy', 'Commit Standards', 'Pull Request Process', 'Merge Policies', 'Release Branches', 'Tagging',
];

export const CODE_REVIEW = [
  'Review Checklists', 'Architecture Validation', 'Security Review', 'Performance Review', 'Accessibility Review', 'Documentation Review',
];

export const STATIC_ANALYSIS = [
  'Linting', 'Formatting', 'Dependency Scanning', 'Secrets Detection', 'License Validation', 'Complexity Analysis',
];

export const SECURE_CODING = [
  'OWASP Standards', 'Input Validation', 'Output Encoding', 'Authentication', 'Authorization', 'Secrets Management',
];

export const TESTING_REQUIREMENTS = [
  'Unit Tests', 'Integration Tests', 'Contract Tests', 'End-to-End Tests', 'AI Validation', 'Marketplace Validation', 'Regression Testing',
];

export const DEPENDENCY_GOVERNANCE = [
  'Approved Libraries', 'Version Policies', 'Security Updates', 'Deprecation Policies', 'License Compliance',
];

export const TECHNICAL_DEBT = ['Debt Register', 'Ownership', 'Prioritization', 'Resolution Plans', 'Business Impact'];

export const CI_GATES = [
  'Build Validation', 'Lint Validation', 'Security Validation', 'Performance Validation',
  'Accessibility Validation', 'Coverage Thresholds', 'Architecture Compliance',
];

export const ENGINEERING_METRICS = [
  'Code Quality', 'Coverage', 'Technical Debt', 'Bug Density', 'Deployment Frequency', 'Lead Time', 'AI Code Utilization',
];

export const KNOWLEDGE_SHARING = ['Engineering Wiki', 'Coding Standards', 'Playbooks', 'Architecture Sessions', 'Mentorship'];

export const ARCHITECTURE_STAGES = [
  'Engineering Council', 'Coding Constitution', 'Backend & Frontend', 'Database & API',
  'AI & Git Governance', 'Code Review & Analysis', 'Secure Coding & Testing', 'CI Gates & Metrics',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  architectureGovernance: '/platform/architecture-governance',
  enterpriseDevOps: '/platform/enterprise-devops',
  enterpriseQuality: '/platform/enterprise-quality',
};

export const DEMO_COUNCIL: CouncilMemberRow[] = [
  { role: 'CTO', responsibility: 'Engineering strategy & investment', approvalScope: 'Executive escalation' },
  { role: 'Chief Enterprise Architect', responsibility: 'Architecture compliance in code', approvalScope: 'L3+ change approval' },
  { role: 'Engineering Manager', responsibility: 'Team standards & delivery', approvalScope: 'Sprint merge approval' },
  { role: 'AI Engineering Lead', responsibility: 'AI-assisted development governance', approvalScope: 'AI code review gate' },
  { role: 'Security Engineering Lead', responsibility: 'Secure coding & OWASP compliance', approvalScope: 'Security gate veto' },
  { role: 'DevOps Lead', responsibility: 'CI/CD gates & deployment', approvalScope: 'Pipeline policy approval' },
];

export const DEMO_CODING: CodingStandardRow[] = [
  { standard: 'Naming Conventions', rule: 'Go: camelCase exports, snake_case files', status: 'enforced' },
  { standard: 'Folder Structures', rule: 'Clean Architecture: cmd/internal/service/handler', status: 'enforced' },
  { standard: 'Package Structures', rule: 'Feature-sliced React with shared UI packages', status: 'enforced' },
  { standard: 'Documentation Standards', rule: 'Every module: purpose, API, testing, deployment', status: 'mandatory' },
];

export const DEMO_STACK: StackStandardRow[] = [
  { stack: 'Backend', standard: 'Go 1.22+ with dependency injection', status: 'enforced' },
  { stack: 'Backend', standard: 'Structured logging + OpenTelemetry traces', status: 'enforced' },
  { stack: 'Backend', standard: 'Repository pattern + outbox for events', status: 'enforced' },
  { stack: 'Frontend', standard: 'React 18 + TypeScript strict mode', status: 'enforced' },
  { stack: 'Frontend', standard: 'Component architecture with design tokens', status: 'enforced' },
  { stack: 'Frontend', standard: 'WCAG 2.2 AA on all interactive components', status: 'mandatory' },
];

export const DEMO_DATABASE_API: DomainStandardRow[] = [
  { domain: 'Database', standard: 'Tenant-scoped schema + audit columns', status: 'enforced' },
  { domain: 'Database', standard: 'Migration review for L3+ schema changes', status: 'enforced' },
  { domain: 'API', standard: 'RESTful + OpenAPI documented', status: 'enforced' },
  { domain: 'API', standard: 'Versioned endpoints with pagination', status: 'enforced' },
  { domain: 'API', standard: 'Rate limiting + idempotent mutations', status: 'enforced' },
];

export const DEMO_AI_GIT: PolicyRow[] = [
  { area: 'AI Engineering', policy: 'Human review required for AI-generated code', status: 'enforced' },
  { area: 'AI Engineering', policy: 'Architecture compliance check on AI PRs', status: 'enforced' },
  { area: 'Git Governance', policy: 'Trunk-based with feature branches', status: 'enforced' },
  { area: 'Git Governance', policy: 'Conventional commits + PR templates', status: 'enforced' },
  { area: 'Git Governance', policy: 'Squash merge to main with CI gates', status: 'enforced' },
];

export const DEMO_REVIEW: ReviewCheckRow[] = [
  { area: 'Code Review', check: 'Architecture validation checklist', status: 'mandatory', score: 100 },
  { area: 'Code Review', check: 'Security review on auth/data paths', status: 'mandatory', score: 100 },
  { area: 'Static Analysis', check: 'golangci-lint + ESLint passing', status: 'passing', score: 100 },
  { area: 'Static Analysis', check: 'Secrets detection + license scan', status: 'passing', score: 100 },
  { area: 'Static Analysis', check: 'Complexity analysis under threshold', status: 'passing', score: 96 },
];

export const DEMO_SECURE_TEST: SecureTestRow[] = [
  { domain: 'Secure Coding', requirement: 'OWASP Top 10 compliance', status: 'validated', coverage: '100%' },
  { domain: 'Secure Coding', requirement: 'Input validation on all endpoints', status: 'enforced', coverage: '100%' },
  { domain: 'Testing', requirement: 'Unit tests on all business logic', status: 'mandatory', coverage: '>80%' },
  { domain: 'Testing', requirement: 'Integration + contract tests', status: 'mandatory', coverage: '>70%' },
  { domain: 'Testing', requirement: 'E2E on critical user journeys', status: 'mandatory', coverage: 'core paths' },
];

export const DEMO_CI_METRICS: CIMetricRow[] = [
  { category: 'CI Gate', item: 'Build validation', value: 'passing', status: 'green' },
  { category: 'CI Gate', item: 'Coverage threshold', value: '82%', status: 'above_min' },
  { category: 'Dependency', item: 'Approved libraries', value: '248 registered', status: 'compliant' },
  { category: 'Technical Debt', item: 'Open debt items', value: '38 tracked', status: 'managed' },
  { category: 'Metrics', item: 'Deployment frequency', value: '4.2/week', status: 'healthy' },
  { category: 'Metrics', item: 'AI code utilization', value: '34%', status: 'reviewed' },
  { category: 'Knowledge', item: 'Engineering wiki articles', value: '186 published', status: 'current' },
];

export function buildEngineeringDashboard() {
  return {
    council_roles: COUNCIL_ROLES.length,
    coding_standards: CODING_STANDARDS.length,
    ci_gates: CI_GATES.length,
    engineering_metrics: ENGINEERING_METRICS.length,
    testing_requirements: TESTING_REQUIREMENTS.length,
  };
}
